/**
 * 日期区间选择器 — 14 个快捷区间 + 双月日历面板。
 * 由 design/Novel-admin/src/datepicker.jsx 1:1 移植：JSX / className / inline style 原样保留，
 * 仅做 JS→TS（加严格类型）+ window 全局引用 → ESM import 的机械转换。
 * 导出 DateRangeButton（触发按钮 + portal 浮层）与 DateRangePanel（纯面板）。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icons } from '~/lib/icons';

// 快捷区间：[显示文案, key]
const QUICK_RANGES: [string, string][] = [
  ['今天', 'today'],
  ['昨天', 'yesterday'],
  ['近3天', '3d'],
  ['近7天', '7d'],
  ['近14天', '14d'],
  ['近30天', '30d'],
  ['本周', 'thisWeek'],
  ['上周', 'lastWeek'],
  ['本月', 'thisMonth'],
  ['上月', 'lastMonth'],
  ['近60天', '60d'],
  ['近90天', '90d'],
  ['近半年', '180d'],
  ['今年', 'ytd'],
];

const DOW = ['一', '二', '三', '四', '五', '六', '日'];
const MONTHS = [
  '1月',
  '2月',
  '3月',
  '4月',
  '5月',
  '6月',
  '7月',
  '8月',
  '9月',
  '10月',
  '11月',
  '12月',
];

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function sameDay(a: Date | null | undefined, b: Date | null | undefined): boolean {
  return (
    !!a &&
    !!b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function fmt(d: Date | null | undefined): string {
  if (!d) return '';
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
}

function computeRange(key: string, referenceDate?: Date): [Date, Date] {
  const today = startOfDay(referenceDate ?? new Date());
  let s = new Date(today);
  let e = new Date(today);
  if (key === 'today') {
    /* 默认即今天 */
  } else if (key === 'yesterday') {
    s = addDays(today, -1);
    e = addDays(today, -1);
  } else if (key === '3d') {
    s = addDays(today, -2);
  } else if (key === '7d') {
    s = addDays(today, -6);
  } else if (key === '14d') {
    s = addDays(today, -13);
  } else if (key === '30d') {
    s = addDays(today, -29);
  } else if (key === '60d') {
    s = addDays(today, -59);
  } else if (key === '90d') {
    s = addDays(today, -89);
  } else if (key === '180d') {
    s = addDays(today, -179);
  } else if (key === 'thisWeek') {
    const dow = (today.getDay() + 6) % 7; // Mon=0
    s = addDays(today, -dow);
  } else if (key === 'lastWeek') {
    const dow = (today.getDay() + 6) % 7;
    e = addDays(today, -dow - 1);
    s = addDays(e, -6);
  } else if (key === 'thisMonth') {
    s = new Date(today.getFullYear(), today.getMonth(), 1);
  } else if (key === 'lastMonth') {
    s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    e = new Date(today.getFullYear(), today.getMonth(), 0);
  } else if (key === 'ytd') {
    s = new Date(today.getFullYear(), 0, 1);
  }
  return [s, e];
}

function detectQuick(
  start: Date | null | undefined,
  end: Date | null | undefined,
  referenceDate?: Date,
): string | null {
  if (!start || !end) return null;
  for (const [, key] of QUICK_RANGES) {
    const [s, e] = computeRange(key, referenceDate);
    if (sameDay(s, start) && sameDay(e, end)) return key;
  }
  return null;
}

interface MonthGridProps {
  referenceDate?: Date;
  year: number;
  month: number;
  start: Date | null;
  end: Date | null;
  hover: Date | null;
  onClick: (date: Date) => void;
  onHover: (date: Date) => void;
}

function MonthGrid({
  year,
  month,
  start,
  end,
  hover,
  onClick,
  onHover,
  referenceDate,
}: MonthGridProps) {
  const today = startOfDay(referenceDate ?? new Date());
  const first = new Date(year, month, 1);
  // Mon=0..Sun=6 offset for leading blank cells
  const startDow = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevMonthDays = new Date(year, month, 0).getDate();

  const cells: { date: Date; outside: boolean }[] = [];
  for (let i = startDow; i > 0; i--) {
    cells.push({ date: new Date(year, month - 1, prevMonthDays - i + 1), outside: true });
  }
  for (let d = 1; d <= daysInMonth; d++)
    cells.push({ date: new Date(year, month, d), outside: false });
  let next = 1;
  while (cells.length < 42) cells.push({ date: new Date(year, month + 1, next++), outside: true });

  // Effective end for range highlight: if start picked but no end, use hover
  const lo =
    start && end
      ? start <= end
        ? start
        : end
      : start && hover
        ? start <= hover
          ? start
          : hover
        : null;
  const hi =
    start && end
      ? start <= end
        ? end
        : start
      : start && hover
        ? start <= hover
          ? hover
          : start
        : null;

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        className="hstack"
        style={{
          justifyContent: 'center',
          height: 32,
          fontSize: 14,
          fontWeight: 500,
          color: 'var(--fg-strong)',
          letterSpacing: '-0.005em',
        }}
      >
        {year}年 {MONTHS[month]}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          padding: '8px 0 6px',
          borderBottom: '1px solid var(--border)',
        }}
      >
        {DOW.map((d, i) => (
          <div key={i} style={{ textAlign: 'center', fontSize: 11.5, color: 'var(--fg-muted)' }}>
            {d}
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {cells.map((c, i) => {
          const t = c.date.getTime();
          const isStart = start && t === start.getTime();
          const isEnd = end && t === end.getTime();
          const inRange = lo && hi && t >= lo.getTime() && t <= hi.getTime();
          const isPickedEdge = isStart || isEnd || (start && !end && sameDay(c.date, start));
          const isToday = sameDay(c.date, today);
          const col = i % 7;
          const isLeftEdge = inRange && (col === 0 || (lo && t === lo.getTime()));
          const isRightEdge = inRange && (col === 6 || (hi && t === hi.getTime()));
          return (
            <div
              key={i}
              onClick={() => !c.outside && onClick(c.date)}
              onMouseEnter={() => !c.outside && onHover(c.date)}
              style={{
                height: 36,
                position: 'relative',
                cursor: c.outside ? 'default' : 'pointer',
                background: inRange && !isPickedEdge ? 'var(--accent-soft)' : 'transparent',
                borderTopLeftRadius: isLeftEdge ? 8 : 0,
                borderBottomLeftRadius: isLeftEdge ? 8 : 0,
                borderTopRightRadius: isRightEdge ? 8 : 0,
                borderBottomRightRadius: isRightEdge ? 8 : 0,
                transition: 'background 0.08s ease',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 3,
                  borderRadius: 999,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                  fontFamily: 'var(--font-display)',
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: isPickedEdge ? 600 : isToday ? 600 : 400,
                  color: isPickedEdge ? 'white' : c.outside ? 'var(--fg-faint)' : 'var(--fg)',
                  background: isPickedEdge ? 'var(--accent)' : 'transparent',
                  transition: 'background 0.08s',
                }}
                onMouseEnter={(e) => {
                  if (!c.outside && !isPickedEdge)
                    e.currentTarget.style.background = 'var(--bg-muted)';
                }}
                onMouseLeave={(e) => {
                  if (!isPickedEdge) e.currentTarget.style.background = 'transparent';
                }}
              >
                {c.date.getDate()}
              </div>
              {isToday && !isPickedEdge && (
                <span
                  style={{
                    position: 'absolute',
                    bottom: 4,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 4,
                    height: 4,
                    borderRadius: 999,
                    background: 'var(--accent)',
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export interface DateRangePanelProps {
  /** 报表日历的今天；Date 仅承载年月日，默认沿用浏览器本地今天。 */
  referenceDate?: Date;
  initialStart: Date | null;
  initialEnd: Date | null;
  onApply: (range: [Date, Date]) => void;
  onCancel: () => void;
}

export function DateRangePanel({
  initialStart,
  initialEnd,
  referenceDate,
  onApply,
  onCancel,
}: DateRangePanelProps) {
  const [start, setStart] = useState<Date | null>(initialStart);
  const [end, setEnd] = useState<Date | null>(initialEnd);
  const [hover, setHover] = useState<Date | null>(null);
  const init = initialStart || referenceDate || new Date();
  const [viewY, setViewY] = useState(init.getFullYear());
  const [viewM, setViewM] = useState(init.getMonth());
  const [quick, setQuick] = useState<string | null>(
    detectQuick(initialStart, initialEnd, referenceDate),
  );

  const stepMonth = (delta: number) => {
    const d = new Date(viewY, viewM + delta, 1);
    setViewM(d.getMonth());
    setViewY(d.getFullYear());
  };
  const stepYear = (delta: number) => setViewY(viewY + delta);

  const pickDay = (d: Date) => {
    setQuick(null);
    if (!start || (start && end)) {
      setStart(d);
      setEnd(null);
    } else if (d < start) {
      setEnd(start);
      setStart(d);
    } else {
      setEnd(d);
    }
  };

  const useQuick = (key: string) => {
    const [s, e] = computeRange(key, referenceDate);
    setStart(s);
    setEnd(e);
    setQuick(key);
    setHover(null);
    setViewY(s.getFullYear());
    setViewM(s.getMonth());
  };

  const nextMonth = useMemo(() => {
    const d = new Date(viewY, viewM + 1, 1);
    return { y: d.getFullYear(), m: d.getMonth() };
  }, [viewY, viewM]);

  return (
    <div
      style={{
        background: 'var(--bg-elev)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        boxShadow: 'var(--shadow-lg)',
        width: 720,
        display: 'flex',
        overflow: 'hidden',
        animation: 'fade-in 0.12s ease',
      }}
    >
      {/* Left quick options */}
      <div
        style={{
          width: 196,
          borderRight: '1px solid var(--border)',
          padding: 10,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 6,
          gridAutoRows: 'min-content',
          background: 'var(--bg-subtle)',
        }}
      >
        {QUICK_RANGES.map(([label, key]) => {
          const active = quick === key;
          return (
            <button
              key={key}
              onClick={() => useQuick(key)}
              className="focusable"
              style={{
                padding: '8px 4px',
                border: 0,
                background: active ? 'var(--bg-elev)' : 'var(--bg-elev)',
                color: active ? 'var(--accent-fg)' : 'var(--fg)',
                borderRadius: 6,
                cursor: 'pointer',
                font: 'inherit',
                fontSize: 12.5,
                fontWeight: active ? 500 : 400,
                boxShadow: active ? '0 0 0 1.5px var(--accent) inset' : 'var(--shadow-xs)',
                transition: 'box-shadow 0.1s',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>
      {/* Right calendars */}
      <div style={{ flex: 1, padding: 14, minWidth: 0 }}>
        {/* Header with nav */}
        <div className="hstack" style={{ marginBottom: 4 }}>
          <button
            className="btn btn-ghost btn-icon"
            onClick={() => stepYear(-1)}
            title="上一年"
            style={{ width: 26, height: 26 }}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m11 17-5-5 5-5" />
              <path d="m18 17-5-5 5-5" />
            </svg>
          </button>
          <button
            className="btn btn-ghost btn-icon"
            onClick={() => stepMonth(-1)}
            title="上个月"
            style={{ width: 26, height: 26 }}
          >
            <Icons.IconChevLeft size={13} />
          </button>
          <div style={{ flex: 1 }} />
          <button
            className="btn btn-ghost btn-icon"
            onClick={() => stepMonth(1)}
            title="下个月"
            style={{ width: 26, height: 26 }}
          >
            <Icons.IconChevRight size={13} />
          </button>
          <button
            className="btn btn-ghost btn-icon"
            onClick={() => stepYear(1)}
            title="下一年"
            style={{ width: 26, height: 26 }}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m13 17 5-5-5-5" />
              <path d="m6 17 5-5-5-5" />
            </svg>
          </button>
        </div>

        <div
          className="hstack"
          style={{ gap: 16, alignItems: 'flex-start' }}
          onMouseLeave={() => setHover(null)}
        >
          <MonthGrid
            referenceDate={referenceDate}
            year={viewY}
            month={viewM}
            start={start}
            end={end}
            hover={hover}
            onClick={pickDay}
            onHover={setHover}
          />
          <MonthGrid
            referenceDate={referenceDate}
            year={nextMonth.y}
            month={nextMonth.m}
            start={start}
            end={end}
            hover={hover}
            onClick={pickDay}
            onHover={setHover}
          />
        </div>

        {/* Footer */}
        <div
          className="hstack"
          style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)', gap: 8 }}
        >
          <div className="hstack" style={{ gap: 6 }}>
            <input
              className="input mono"
              value={fmt(start)}
              readOnly
              style={{ width: 110, fontSize: 11.5, height: 28, textAlign: 'center' }}
            />
            <span style={{ color: 'var(--fg-muted)', fontSize: 12 }}>—</span>
            <input
              className="input mono"
              value={fmt(end)}
              readOnly
              style={{ width: 110, fontSize: 11.5, height: 28, textAlign: 'center' }}
            />
            {start && end && (
              <span
                className="mono"
                style={{ marginLeft: 10, fontSize: 11, color: 'var(--fg-muted)' }}
              >
                · 共 {Math.round((end.getTime() - start.getTime()) / 86400000) + 1} 天
              </span>
            )}
          </div>
          <div style={{ flex: 1 }} />
          <button className="btn btn-sm" onClick={onCancel}>
            取消
          </button>
          <button
            className="btn btn-sm btn-accent"
            disabled={!start || !end}
            onClick={() => {
              if (start && end) onApply([start, end]);
            }}
          >
            <Icons.IconCheck size={11} />
            应用
          </button>
        </div>
      </div>
    </div>
  );
}

export interface DateRangeButtonProps {
  /** 快捷范围及今天标记使用的日历参考日；手选日期不转换。 */
  referenceDate?: Date;
  value?: [Date, Date];
  onChange?: (range: [Date, Date]) => void;
  label?: string;
  defaultQuick?: string;
  buttonStyle?: React.CSSProperties;
  buttonClass?: string;
  showIcon?: boolean;
}

export function DateRangeButton({
  value,
  onChange,
  referenceDate,
  label,
  defaultQuick = '30d',
  buttonStyle,
  buttonClass = 'btn',
  showIcon = true,
}: DateRangeButtonProps) {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState<[Date, Date]>(() => {
    if (value?.[0] && value[1]) return value;
    return computeRange(defaultQuick, referenceDate);
  });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    const update = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      const w = 720;
      const margin = 8;
      let left = r.left;
      if (left + w > window.innerWidth - margin) left = window.innerWidth - w - margin;
      if (left < margin) left = margin;
      let top = r.bottom + 6;
      if (top + 360 > window.innerHeight - margin) top = r.top - 360 - 6;
      setPos({ top, left });
    };
    update();
    const onClick = (e: MouseEvent) => {
      const dlg = document.getElementById('date-range-portal');
      const target = e.target as Node;
      if (dlg && !dlg.contains(target) && !triggerRef.current?.contains(target)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const apply = (r: [Date, Date]) => {
    setRange(r);
    setOpen(false);
    onChange?.(r);
  };

  const displayLabel = (() => {
    const q = detectQuick(range[0], range[1], referenceDate);
    if (q) {
      const found = QUICK_RANGES.find((qr) => qr[1] === q);
      if (found) return found[0];
    }
    return `${fmt(range[0]).slice(2)} — ${fmt(range[1]).slice(2)}`;
  })();

  return (
    <>
      <button
        ref={triggerRef}
        className={buttonClass}
        onClick={() => setOpen(!open)}
        style={buttonStyle}
      >
        {showIcon && <Icons.IconCalendar size={12} />}
        <span>{label || displayLabel}</span>
        <Icons.IconChevDown size={11} />
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            id="date-range-portal"
            style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 100 }}
          >
            <DateRangePanel
              referenceDate={referenceDate}
              initialStart={range[0]}
              initialEnd={range[1]}
              onApply={apply}
              onCancel={() => setOpen(false)}
            />
          </div>,
          document.body,
        )}
    </>
  );
}
